import { api } from "../../lib/api";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtDateTime } from "../../lib/format";
import { useAdminData, Pager, FilterSelect } from "./shared";

const FILTERS = [["", "All"], ["deletion_pending", "Pending"], ["purge_due", "Due for purge"], ["deletion_failed", "Failed"], ["deleted", "Completed"]];
const STATUS_CHIP = {
  deletion_pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  purge_due: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  deletion_failed: "bg-red-500/15 text-red-400 border-red-500/30",
  deleted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};
const STATUS_LABEL = { deletion_pending: "DELETION PENDING", purge_due: "PURGE DUE", deletion_failed: "DELETION FAILED", deleted: "DELETED" };

function Row({ k, v, testid }) {
  return <p className="text-[11px] text-zinc-500"><span className="text-zinc-600">{k}:</span> <span className="text-zinc-300 break-all" data-testid={testid}>{v || "—"}</span></p>;
}

export default function AdminDeletions() {
  const { data, error, page, setPage, filters, setFilters, reload } = useAdminData(api.adminAccountDeletions);
  const rows = data?.items || [];

  return (
    <div data-testid="admin-deletions-page">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <FilterSelect value={filters.status || ""} onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                      options={FILTERS} label="Filter by deletion status" testid="deletions-status-filter" />
      </div>
      {error ? <ErrorState body={error} onRetry={reload} /> : !data ? <PageLoading label="Loading deletion audit…" /> : rows.length === 0 ? (
        <div className="card-dark p-12 text-center text-sm text-zinc-500" data-testid="deletions-empty">No account deletions found.</div>
      ) : (
        <>
          <div className="space-y-3" data-testid="deletions-list">
            {rows.map((d) => (
              <div key={d.deletion_id} className="card-dark p-5" data-testid={`deletion-${d.deletion_id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_CHIP[d.status] || ""}`}
                        data-testid={`deletion-status-${d.deletion_id}`}>{STATUS_LABEL[d.status] || d.status}</span>
                  <p className="font-semibold text-sm" data-testid={`deletion-user-${d.deletion_id}`}>{d.email_masked}</p>
                  <p className="text-[11px] text-zinc-500">{(d.roles || []).join(" · ")}</p>
                  <p className="ml-auto text-[11px] text-zinc-600 font-mono">{d.user_id}</p>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                  <Row k="Requested" v={fmtDateTime(d.requested_at)} />
                  <Row k="Requested by" v={d.requested_by} />
                  <Row k="Reason" v={d.reason} />
                  <Row k="Purge due" v={fmtDateTime(d.purge_due_at)} />
                  <Row k="Sessions revoked" v={d.sessions_revoked_at ? fmtDateTime(d.sessions_revoked_at) : "—"} />
                  <Row k="Purge attempts" v={String(d.purge_attempts ?? 0)} />
                  <Row k="Last attempt" v={d.last_attempt_at ? fmtDateTime(d.last_attempt_at) : "—"} />
                  <Row k="Completed" v={d.purge_completed_at ? fmtDateTime(d.purge_completed_at) : "—"} />
                  <Row k="Error" v={d.purge_error} testid={`deletion-error-${d.deletion_id}`} />
                </div>
                <p className="mt-2 text-[11px] text-zinc-600">Retained: {(d.retained_categories || []).join("; ")}</p>
                <div className="mt-2 border-t border-[#1F2328] pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-600">Events</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1" data-testid={`deletion-events-${d.deletion_id}`}>
                    {(d.events || []).map((e, i) => (
                      <span key={i} className="text-[11px] text-zinc-500">{e.event} <span className="text-zinc-600">· {fmtDateTime(e.at)}</span></span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} testid="deletions-pager" />
        </>
      )}
    </div>
  );
}
